<script>

import SocialNetworkImage from "@/components/socialNetworkImage";
import LetterInCircle from "@/components/letterInCircle";
import CartPreviewPdf from "@/helpers/cart/CartPreviewPdf";

export default {
  components: {LetterInCircle, SocialNetworkImage},
  props: {
    influencerCartItems: {
      type: Array,
      required: true
    },
  },
  data() {
    return {
      rowsByInfluencer: {},
      expandedInfluencers: {}
    }
  },
  methods: {
    amountChanged(influencerId, pricePackageId, newAmount) {
      this.$emit('amountChanged', influencerId, pricePackageId, parseInt(newAmount))
    },
    removePricePackage(influencerId, pricePackageId) {
      const rowIndex = this.rowsByInfluencer[influencerId].findIndex(row => row.pricePackage._id === pricePackageId);
      this.rowsByInfluencer[influencerId].splice(rowIndex, 1)

      this.$emit('pricePackageRemoved', influencerId, pricePackageId)
    },
    calculateTotalPrice(influencerId) {
      return this.rowsByInfluencer[influencerId].reduce((result, row) => {
        if (!row.lead || !row.price) {
          return result
        }
        result += (row.price * row.amount)
        return result
      }, 0)
    },
    calculateTotalReach(influencerId) {
      return this.rowsByInfluencer[influencerId].reduce((result, row) => {
        if (!row.lead || !row.reach) {
          return result
        }
        result += (row.reach * row.amount)
        return result
      }, 0)
    },
    calculateTotalCpm(influencerId) {
      const totalReach = this.calculateTotalReach(influencerId);
      if (!totalReach) {
        return 0
      }
      return Math.round((1000 / totalReach) * this.calculateTotalPrice(influencerId))
    },
    calculateTotalPosts(influencerId) {
      return this.rowsByInfluencer[influencerId].reduce((result, row) => {
        if (row.quantity) {
          result += (row.quantity * row.amount)
        }
        if (row.postCount) {
          result += (row.postCount * row.amount)
        }

        return result;
      }, 0)
    },
    isSinglePrice(pricePackage) {
      return pricePackage.price_package_items.length === 1 && pricePackage.price_package_items[0].quantity <= 1;
    },
    calculateReach(influencerCartItem, pricePackageItem) {
      let reach = 0;

      const socialNetwork = pricePackageItem.social_network;
      const socialNetworkAccount = influencerCartItem.socialNetworkAccounts[socialNetwork]
      if (socialNetworkAccount) {
        reach = socialNetworkAccount.last_30_days_avg_reach_per_post ?? socialNetworkAccount.last_30_days_new_users ?? 0;
      }

      return reach * pricePackageItem.quantity;
    },
    async downloadCartPreview(campaignCartItem) {
      try {
        this.$store.commit('loader/START_LOADING');
        const cartPreviewPdf = new CartPreviewPdf(this.influencerCartItems, this.rowsByInfluencer);
        await cartPreviewPdf.download(`${campaignCartItem.campaignInternalName}.pdf`);
      } finally {
        this.$store.commit('loader/END_LOADING');
      }
    },
  },
  created() {
    let colorIndex = 0;

    for (let influencerCartItem of this.influencerCartItems) {
      this.$set(this.expandedInfluencers, influencerCartItem.influencerId, true)
      this.$set(this.rowsByInfluencer, influencerCartItem.influencerId, [])

      Object.values(influencerCartItem.pricePackageCartItems).forEach(pricePackageCartItem => {
        const pricePackage = pricePackageCartItem.pricePackage
        const amount = pricePackageCartItem.amount
        if (this.isSinglePrice(pricePackage)) {
          let reach = this.calculateReach(influencerCartItem, pricePackage.price_package_items[0])

          let cpm;
          if (reach) {
            cpm = ((1000 / reach) * pricePackage.total_gross_price)
          }

          const extra = [];
          if (pricePackage.price_package_items[0].giveaway) {
            extra.push('giveaway');
          }
          if (pricePackage.price_package_items[0].event) {
            extra.push('event');
          }

          this.rowsByInfluencer[influencerCartItem.influencerId].push({
            colorIndex: colorIndex,
            pricePackage: pricePackage,
            icon: pricePackage.price_package_items[0].social_network,
            socialNetwork: pricePackage.price_package_items[0].social_network,
            postType: pricePackage.price_package_items[0].post_type,
            quantity: pricePackage.price_package_items[0].quantity,
            extra: extra,
            reach: reach,
            cpm: cpm,
            price: pricePackage.total_gross_price,
            currency: pricePackage.currency,
            lead: true,
            amount: amount,
            expanded: false
          })
        } else { // Package

          let pricePackageReach = 0;
          let postCount = 0;

          const pricePackageItems = []
          pricePackage.price_package_items.forEach(pricePackageItem => {
            const reach = this.calculateReach(influencerCartItem, pricePackageItem)
            pricePackageReach += reach
            postCount += pricePackageItem.quantity

            const extra = [];
            if (pricePackageItem.giveaway) {
              extra.push('giveaway');
            }
            if (pricePackageItem.event) {
              extra.push('event');
            }

            pricePackageItems.push({
              colorIndex: colorIndex,
              pricePackage: pricePackage,
              icon: pricePackageItem.social_network,
              socialNetwork: pricePackageItem.social_network,
              postType: pricePackageItem.post_type,
              quantity: pricePackageItem.quantity,
              extra: extra,
              reach: reach,
              lead: false,
            })
          })

          let pricePackageCpm;
          if (pricePackageReach) {
            pricePackageCpm = ((1000 / pricePackageReach) * pricePackage.total_gross_price)
          }

          this.rowsByInfluencer[influencerCartItem.influencerId].push({
            colorIndex: colorIndex,
            pricePackage: pricePackage,
            icon: null,
            postType: pricePackage.name,
            postCount: postCount,
            reach: pricePackageReach,
            cpm: pricePackageCpm,
            price: pricePackage.total_gross_price,
            currency: pricePackage.currency,
            lead: true,
            expanded: true,
            amount: amount,
            pricePackageItems: pricePackageItems
          })

        }

        colorIndex++;
      })
    }

  }
}
</script>

<template>
  <div class="table-responsive px-2 mb-0">
    <table class="table mb-3">
      <thead>
      <tr >
        <th style="width: 55px" class="border-separator">#</th>
        <th class="border-separator">Post</th>
        <th class="border-separator">Extra</th>
        <th class="border-separator text-right">Reach</th>
        <th class="border-separator text-right">CPM</th>
        <th class="border-separator text-right">Price</th>
        <th class="border-separator text-right">Amount</th>
        <th class="border-separator text-center">Action</th>
      </tr>
      </thead>
      <template v-for="influencerCartItem in influencerCartItems">
        <thead :key="`${influencerCartItem.influencerId}_thead`" class="pb-4">
        <tr style="background: #efefef">
          <td colspan="2" style="width: 10000000000px;" class=" pt-3">
            <div class="d-flex">
              <router-link tag="a" :to="`/client/influencers/${influencerCartItem.influencerId}`">
                <img v-if="influencerCartItem.profilePictureUrl" :src="influencerCartItem.profilePictureUrl" class="avatar-sm rounded-circle">
                <letter-in-circle v-else-if="influencerCartItem.username" class="avatar-sm">{{ influencerCartItem.username.charAt(0) }}</letter-in-circle>
              </router-link>
              <span class="d-flex flex-column px-2">
              <span v-for="socialNetwork in influencerCartItem.getActiveSocialNetworks()" :key="`${socialNetwork.socialNetwork}_${socialNetwork.username}`" class="text-nowrap">
                <social-network-image class="mr-1" style="font-size:1.2em; color: gray!important;" :social-network="socialNetwork.socialNetwork"></social-network-image>
                <router-link tag="a" :to="`/client/influencers/${influencerCartItem.influencerId}`" class="text-dark">
                  {{socialNetwork.username}}
                </router-link>
              </span>
            </span>
            </div>
          </td>
          <td class="">
            <b-badge class="invisible badge-soft-info font-size-11">Giveaway</b-badge>
            <b-badge class="invisible badge-soft-info font-size-11">Event</b-badge>
          </td>
          <td class="text-right ">
            <div class="col-12 mb-0 mb-xl-1 font-size-13 text-muted">
<!--              Reach-->
            </div>
            <div class=" h-100">
              <h6 v-if="calculateTotalReach(influencerCartItem.influencerId)" class="mb-0">{{calculateTotalReach(influencerCartItem.influencerId).toLocaleString()}}</h6>
              <h6 v-else class="mb-0">N/A</h6>
            </div>
          </td>
          <td class="text-right ">
            <div class="col-12 mb-0 mb-xl-1 font-size-13 text-muted">
<!--              CPM-->
            </div>
            <div class=" h-100">
              <h6 v-if="calculateTotalCpm(influencerCartItem.influencerId)" class="mb-0 text-nowrap">&euro;{{ calculateTotalCpm(influencerCartItem.influencerId).toLocaleString() }}</h6>
              <h6 v-else class="mb-0">N/A</h6>
            </div>
          </td>
          <td class="text-right ">
            <div class=" mb-0 mb-xl-1 font-size-13 text-muted">
<!--              Price-->
            </div>
            <div class=" h-100">
              <h6 v-if="calculateTotalPrice(influencerCartItem.influencerId)" class="mb-0 text-nowrap">&euro;{{ calculateTotalPrice(influencerCartItem.influencerId).toLocaleString() }}</h6>
              <h6 v-else class="mb-0">N/A</h6>
            </div>
          </td>
          <td class="text-right ">
            <div class="col-12 mb-0 mb-xl-1 font-size-13 text-muted">
<!--              Posts-->
            </div>
            <div class=" h-100">
              <h6 class="mb-0">{{ calculateTotalPosts(influencerCartItem.influencerId) }}</h6>
            </div>
          </td>
          <td class="text-center ">
            <a class="btn-outline-info btn-sm btn text-nowrap" @click="expandedInfluencers[influencerCartItem.influencerId] = !expandedInfluencers[influencerCartItem.influencerId]">
              <i class="mr-1 fas fa-chevron-left"
                 :class="{'rotating-arrow': expandedInfluencers[influencerCartItem.influencerId], 'rotating-arrow-reverse': !expandedInfluencers[influencerCartItem.influencerId]}"
                 style="vertical-align: middle; font-size:1.35em;"></i>Posts
            </a>
          </td>
        </tr>

        </thead>
        <tbody :key="`${influencerCartItem.influencerId}_tbody`" v-if="expandedInfluencers[influencerCartItem.influencerId]">

        <template v-for="(row, index) in rowsByInfluencer[influencerCartItem.influencerId]">
          <tr :key="index"
              :class="{'stripped-row': (row.colorIndex % 2 === 0), 'has-pointer-cursor': row.pricePackageItems}"
              @click="row.expanded = !row.expanded">

            <th scope="row">
              <social-network-image v-if="row.icon" :social-network="row.icon" style="font-size:1.35em;"></social-network-image>
              <i v-else class="fas fa-chevron-right"
                 :class="{'rotating-arrow': row.expanded, 'rotating-arrow-reverse': !row.expanded}"
                 style="vertical-align: middle; font-size:1.35em;"></i>
            </th>
            <td class="text-capitalize " style="text-overflow: ellipsis;">
                          <span v-if="row.lead">
                            {{ row.socialNetwork }} {{ row.postType | snakeToWords }} <i v-if="row.quantity > 1">&times; {{
                              row.quantity
                            }}</i>
                          </span>
              <span v-else>
                            {{ row.socialNetwork }} {{ row.postType | snakeToWords }} <i v-if="row.quantity > 1">&times; {{
                  row.quantity
                }}</i>
                          </span>
            </td>
            <td>
              <b-badge v-if="row.extra && row.extra.includes('giveaway')" class="badge-soft-info font-size-11 mr-1">Giveaway</b-badge>
              <b-badge v-if="row.extra && row.extra.includes('event')" class="badge-soft-info font-size-11">Event</b-badge>
            </td>
            <td class="text-right">
              {{ row.reach ? row.reach.toLocaleString() : '-' }}
            </td>
            <td class="text-right">
              <span v-if="row.cpm" class="text-nowrap">&euro;{{ row.cpm.toLocaleString() }}</span>
              <span v-else>-</span>
            </td>
            <td class="text-right">
              <span v-if="row.price" class="text-nowrap">&euro;{{ row.price.toLocaleString() }}</span>
              <span v-else>-</span>
            </td>
            <td class="text-right">
              <select v-model="row.amount" style="width: 50px" @click.stop class="form-control custom-select px-2"
                      @change="amountChanged(influencerCartItem.influencerId, row.pricePackage._id, row.amount)">
                <option v-for="amountItem in Array.from(Array(31).keys()).slice(1, 31)" :key="amountItem">
                  {{ amountItem }}
                </option>
              </select>
            </td>
            <td class="text-center">
              <a class="has-pointer-cursor text-danger" @click="removePricePackage(influencerCartItem.influencerId, row.pricePackage._id)">
                <i class="fas fa-times"></i>
              </a>
            </td>


          </tr>

          <template v-for="(pricePackageItem, pricePackageIndex) in row.pricePackageItems">
            <tr v-if="row.expanded" :key="`${row.colorIndex}_${pricePackageIndex}`"
                :class="{'stripped-row': (row.colorIndex % 2 === 0)}" class="no-border">

              <th scope="row">
                <social-network-image v-if="pricePackageItem.icon" :social-network="pricePackageItem.icon" style="font-size:1.35em;"></social-network-image>
              </th>
              <td class="text-capitalize">
                {{ pricePackageItem.socialNetwork }} {{ pricePackageItem.postType | snakeToWords }} <i
                  v-if="pricePackageItem.quantity > 1">&times; {{ pricePackageItem.quantity }}</i>
              </td>
              <td>
                <b-badge v-if="pricePackageItem.extra && pricePackageItem.extra.includes('giveaway')" class=" badge-soft-info font-size-11">
                  Giveaway
                </b-badge>
                <b-badge v-if="pricePackageItem.extra && pricePackageItem.extra.includes('event')" class=" badge-soft-info font-size-11">
                  Event
                </b-badge>
              </td>
              <td class="text-right">
                {{ pricePackageItem.reach ? pricePackageItem.reach.toLocaleString() : '-' }}
              </td>
              <td class="text-right">
                <span v-if="pricePackageItem.cpm">&euro;{{ pricePackageItem.cpm.toLocaleString() }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-right">
                            <span
                                v-if="pricePackageItem.price">&euro;{{ pricePackageItem.price.toLocaleString() }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-center">

              </td>
              <td class="text-center">

              </td>

            </tr>
          </template>

        </template>

        </tbody>
      </template>
    </table>
  </div>
</template>

<style scoped>
.stripped-row {
  /*background: #f9f9f9;*/
}

tr.no-border td {
  border: 0;
}

tr.no-border th {
  border: 0;
}

.rotating-arrow {
  transition: 0.10s all;
  transform: rotate(-90deg);
}

.rotating-arrow-reverse {
  transition: 0.10s all;
}

.border-separator {
  border-bottom: 1px solid black;
}

</style>
